import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Zap, BarChart3, Wind } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import BreathworkExperience from '@/components/breathwork/BreathworkExperience';
import { ContextualNotificationPrompt } from '@/components/notifications/ContextualNotificationPrompt';

type BreathPattern = 'coherence' | 'box' | 'calm' | 'energise' | 'sigh' | 'sleep';
type ExperienceMode = 'selecting' | 'quick' | 'full';

const PATTERN_INFO: Record<BreathPattern, { name: string; description: string }> = {
  coherence: { name: 'Coherence', description: 'Balance & heart-brain harmony' },
  box: { name: 'Box Breathing', description: 'Focus & mental clarity' },
  calm: { name: 'Deep Calm', description: 'Extended exhale for relaxation' },
  energise: { name: 'Energise', description: 'Quick breaths for energy' },
  sigh: { name: 'Physiological Sigh', description: 'Instant stress & anxiety relief' },
  sleep: { name: '4-7-8 Breathing', description: 'Deep relaxation & better sleep' },
};

export default function Breathwork() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<ExperienceMode>('selecting');
  
  // Get pattern from URL query param, default to 'coherence'
  const patternParam = searchParams.get('pattern') as BreathPattern | null;
  const validPatterns: BreathPattern[] = ['coherence', 'box', 'calm', 'energise', 'sigh', 'sleep'];
  const defaultPattern = patternParam && validPatterns.includes(patternParam) ? patternParam : 'coherence';

  const patternInfo = PATTERN_INFO[defaultPattern];

  // Show selection screen
  if (mode === 'selecting') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-background p-6 pb-safe-area-inset-bottom"
      >
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="mb-6">
          <ChevronLeft className="w-6 h-6" />
        </Button>

        <div className="max-w-md mx-auto space-y-6">
          {/* Contextual notification prompt */}
          <ContextualNotificationPrompt context="breathwork" className="mb-4" />

          {/* Header */}
          <div className="text-center space-y-2">
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4">
              <Wind className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-semibold">{patternInfo.name}</h1>
            <p className="text-muted-foreground">{patternInfo.description}</p>
          </div>

          {/* Options */}
          <div className="space-y-4 pt-4">
            <p className="text-sm text-muted-foreground text-center">
              How would you like to practice?
            </p>

            {/* Quick Exercise Option */}
            <Card 
              className="cursor-pointer hover:border-primary/50 transition-colors bg-card/50"
              onClick={() => setMode('quick')}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-6 h-6 text-accent" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium mb-1">Quick Exercise</h3>
                    <p className="text-sm text-muted-foreground">
                      Jump straight into breathing. Perfect when you just need a moment to reset.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Full Experience Option */}
            <Card 
              className="cursor-pointer hover:border-primary/50 transition-colors bg-card/50"
              onClick={() => setMode('full')}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium mb-1">Full Experience with Tracking</h3>
                    <p className="text-sm text-muted-foreground">
                      Track how you feel before and after. See real-time impact on your nervous system and mood.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <BreathworkExperience 
      onClose={() => navigate(-1)} 
      defaultPattern={defaultPattern}
      skipTracking={mode === 'quick'}
    />
  );
}
